import 'yet-another-react-lightbox/styles.css';

import {useSignedUrlWithExpiration} from '@wandb/weave/components/Panel2/useAssetFromArtifact';
import {type File, type Node} from '@wandb/weave/core';
import React, {useState} from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

type ArtifactFileImagePreviewProps = {
  // The selected artifact file node (`opArtifactMembershipFile`), used to fetch
  // a signed URL for the raw image. This is already a `file` node, so we resolve
  // the URL directly (no `opAssetFile`, which is for media-asset nodes).
  fileNode: Node;
  name?: string;
};

const imageStyle = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  top: 0,
  left: 0,
  objectFit: 'contain',
  cursor: 'pointer',
} as const;

/**
 * Image preview for a file in the artifact file browser.
 *
 * The inline preview is scaled to fit the panel (object-fit: contain), but a
 * large image cannot be inspected at full size that way. Clicking the image
 * opens a zoomable, fullscreen lightbox so the full-resolution image can be
 * viewed -- reusing the `yet-another-react-lightbox` pattern already used
 * elsewhere in the app (e.g. CellValueImage, ImageView).
 */
export const ArtifactFileImagePreview = ({
  fileNode,
  name,
}: ArtifactFileImagePreviewProps) => {
  const {signedUrl} = useSignedUrlWithExpiration(
    fileNode as Node<File>,
    60 * 1000
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (signedUrl == null) {
    return <div style={{height: '100%'}} />;
  }

  return (
    <div style={{height: '100%', overflow: 'auto'}}>
      <div style={{position: 'relative', height: '100%'}}>
        <img
          style={imageStyle}
          alt={name ?? 'image preview'}
          src={signedUrl}
          onClick={() => setLightboxOpen(true)}
        />
      </div>
      <Lightbox
        plugins={[Fullscreen, Zoom]}
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        controller={{closeOnBackdropClick: true}}
        slides={[{src: signedUrl}]}
        render={{
          // Only one image, so hide the prev/next navigation buttons.
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
        carousel={{finite: true}}
        zoom={{maxZoomPixelRatio: 5}}
      />
    </div>
  );
};
